import { type ComponentProps, forwardRef, memo, useMemo } from 'react';

import { Check, Filters } from '@air/next-icons';
import { DropdownMenu, type DropdownMenuOption, renderDropdownItems } from '@air/primitive-dropdown-menu';
import { IconButton } from '@air/primitive-icon-button';

import { useRecentlyDeletedItemTypes } from '~/components/RecentlyDeleted/hooks/useRecentlyDeletedItemTypes';
import { useRecentlyDeletedQueryParam } from '~/components/RecentlyDeleted/hooks/useRecentlyDeletedQueryParam';

export const PrivateRecentlyDeletedFilterButton = memo(
  forwardRef<HTMLDivElement, ComponentProps<'div'>>((props, ref) => {
    const deletedItemTypes = useRecentlyDeletedItemTypes();
    const { filteredTypes, setFilterTypes } = useRecentlyDeletedQueryParam();

    const options: DropdownMenuOption[] = useMemo(() => {
      return (
        // If the libraries flag is enabled, show all item types. Otherwise, filter out the library item type.
        deletedItemTypes.map((itemType) => ({
          id: itemType.id,
          label: (
            <div className="flex items-center justify-between gap-2">
              <span>{itemType.label}</span>
              {filteredTypes.includes(itemType.id) && <Check className="w-4 text-blue-10" />}
            </div>
          ),
          onSelect: () => {
            setFilterTypes(itemType.id);
          },
          type: 'item',
        }))
      );
    }, [deletedItemTypes, filteredTypes, setFilterTypes]);

    return (
      <div ref={ref} {...props}>
        <DropdownMenu
          align="end"
          trigger={<IconButton appearance="ghost" color="grey" icon={Filters} label="Filters" />}
        >
          {renderDropdownItems({ options })}
        </DropdownMenu>
      </div>
    );
  }),
);

PrivateRecentlyDeletedFilterButton.displayName = 'PrivateRecentlyDeletedFilterButton';
